import { ApiModule } from '@et/api';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { BrowserModule } from '@angular/platform-browser';
import {
  AuthModule,
  ESCROWTAB_OIDC_APP,
  IdTokenBearerInterceptor,
  escrowtabOAuthInitializerFactory,
} from '@et/auth';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { AppComponent } from './app.component';
import { NavbarComponent } from './navbar/navbar.component';
import { HomeComponent } from './home/home.component';
import { FooterComponent } from './footer/footer.component';
import { DirectivesModule } from '@et/directives';
import { I18nModule } from '@et/i18n';
import { NgxsModule, Store } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { environment } from '@et/environment';
import { NotificationsModule } from '@et/notifications';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { MSALProviders } from './add-config';

import { AppRoutingModule } from './app-routing.module';
import { ProfileState } from '@et/guards';
import { Router } from '@angular/router';
import { ErrorLoggerModule, GlobalErrorHandler } from '@et/error-logger';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AppApiModule } from './app-api.module';
import { INACTIVITY_LOGOUT_CONFIG } from '@et/shared';
import { AuthService } from '@et/auth';

/**
 * `environment.useBetterAuth` is resolved at module-evaluation time (file replacement
 * happens at build time). When true, `MsalRedirectComponent` is excluded from bootstrap
 * so it cannot intercept the Better Auth callback URL (/auth/callback?code=…) and send
 * the user to B2C. When false, it is still bootstrapped so MSAL handles B2C redirect
 * responses as before.
 */
const msalBootstrap = environment.useBetterAuth ? [] : [MsalRedirectComponent];

const oauthImports = environment.useBetterAuth
  ? [
      OAuthModule.forRoot({
        resourceServer: {
          // sendAccessToken disabled: Better Auth issues an opaque access_token,
          // not a JWT. IdTokenBearerInterceptor sends the id_token (RS256 JWT)
          // instead so .NET resource servers can validate it against the JWKS.
          sendAccessToken: false,
        },
      }),
    ]
  : [];

const oauthProviders = environment.useBetterAuth
  ? [
      { provide: OAuthStorage, useFactory: () => localStorage },
      { provide: ESCROWTAB_OIDC_APP, useValue: 'web' as const },
      {
        provide: APP_INITIALIZER,
        useFactory: escrowtabOAuthInitializerFactory,
        deps: [OAuthService, ESCROWTAB_OIDC_APP, OAuthStorage],
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: IdTokenBearerInterceptor,
        multi: true,
      },
    ]
  : [];

@NgModule({
  declarations: [AppComponent, NavbarComponent, HomeComponent, FooterComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent, ...msalBootstrap],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AuthModule,
    DirectivesModule,
    I18nModule,
    NgxsModule.forRoot([ProfileState]),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    AppRoutingModule,
    ApiModule.forRoot({
      rootUrl: `${environment.apiServer.domain}`,
    }),
    NotificationsModule,
    ErrorLoggerModule,
    NgxPermissionsModule.forRoot(),
    AppApiModule,
    ...oauthImports,
  ],
  providers: [
    {
      provide: INACTIVITY_LOGOUT_CONFIG,
      useFactory: (authService: AuthService, store: Store) => ({
        onLogout: () => authService.logout(),
        // isEnabled: () => environment.environment !== 'dev',
        isEnabled: () => {
          if (environment.environment === 'dev') return false;
          const company = store.selectSnapshot(ProfileState.getCompany);
          if (!company?.hqState) return false;
          const enableStates = ['North Carolina', 'NC'];
          return enableStates.includes(company.hqState);
        },
      }),
      deps: [AuthService, Store],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ...MSALProviders,
    ...oauthProviders,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
